import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Search, UserMinus, Users } from 'lucide-react';
import { api } from '../../../lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { iniciales } from './constantes';

/*
 * Elegir a una persona de la organización (o del equipo del buzón, si lo tiene).
 *
 *   <SelectorPersona formId valor={persona|null} onElegir={(persona|null) => …} permitirNadie>
 *     <button>…</button>        ← el disparador
 *   </SelectorPersona>
 *
 * `persona` = {id, nombre, correo} o, si es un equipo (grupo de la organización), {id, nombre, correo,
 * tipo: "grupo", miembros}. Las personas las da el servidor (GET /buzon/personas), que ya limita al
 * equipo cuando el buzón tiene uno; los grupos se ofrecen siempre salvo con `conGrupos={false}` (el
 * equipo de rotación del buzón es de personas).
 */

export function Avatar({ persona, tam = 24 }) {
  if (!persona) return null;
  if (persona.tipo === 'grupo') {
    return (
      <span className="bz-avatar is-grupo" style={{ width: tam, height: tam }} aria-hidden="true">
        <Users style={{ width: Math.round(tam * 0.55), height: Math.round(tam * 0.55) }} />
      </span>
    );
  }
  return (
    <span className="bz-avatar" style={{ width: tam, height: tam, fontSize: Math.round(tam * 0.42) }} aria-hidden="true">
      {iniciales(persona.nombre || persona.correo)}
    </span>
  );
}

export default function SelectorPersona({
  formId, valor, onElegir, permitirNadie = false, textoNadie = 'Sin asignar', children, align = 'start', abiertoInicial = false,
  conGrupos = true,
}) {
  const [abierto, setAbierto] = useState(abiertoInicial);
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState({ cargando: false, personas: [], grupos: [], soloEquipo: false });
  const entrada = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    let vivo = true;
    setEstado((e) => ({ ...e, cargando: true }));
    const t = setTimeout(() => {
      api.get('/buzon/personas', { params: { form_id: formId || undefined, q: q || undefined } })
        .then(({ data }) => vivo && setEstado({
          cargando: false, personas: data.personas || [], grupos: conGrupos ? data.grupos || [] : [], soloEquipo: !!data.solo_equipo,
        }))
        .catch(() => vivo && setEstado({ cargando: false, personas: [], grupos: [], soloEquipo: false }));
    }, q ? 220 : 0);
    return () => { vivo = false; clearTimeout(t); };
  }, [abierto, q, formId, conGrupos]);

  const elegir = (p) => {
    setAbierto(false);
    setQ('');
    onElegir?.(p);
  };

  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        className="bz-selector p-0 w-[290px]"
        onOpenAutoFocus={(e) => { e.preventDefault(); entrada.current?.focus(); }}
      >
        <div className="bz-selector-buscar">
          <Search className="h-4 w-4" aria-hidden="true" />
          <input
            ref={entrada}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={estado.soloEquipo ? 'Buscar en el equipo' : 'Buscar persona'}
            aria-label="Buscar persona"
          />
          {estado.cargando && <Loader2 className="h-4 w-4 od-spin" aria-hidden="true" />}
        </div>
        <div className="bz-selector-lista od-scroll" role="listbox" aria-label="Personas">
          {permitirNadie && (
            <button type="button" role="option" aria-selected={!valor} className="bz-selector-fila" onClick={() => elegir(null)}>
              <span className="bz-avatar is-vacio" aria-hidden="true"><UserMinus className="h-3.5 w-3.5" /></span>
              <span className="bz-selector-nombre">{textoNadie}</span>
              {!valor && <Check className="h-4 w-4" aria-hidden="true" />}
            </button>
          )}
          {estado.grupos.length > 0 && <p className="bz-selector-seccion">Equipos</p>}
          {estado.grupos.map((g) => (
            <button
              key={g.id}
              type="button"
              role="option"
              aria-selected={valor?.id === g.id}
              className="bz-selector-fila"
              onClick={() => elegir(g)}
              title="Lo ven y lo trabajan todos sus miembros; cualquiera puede tomarlo"
            >
              <Avatar persona={g} tam={28} />
              <span className="min-w-0 flex-1">
                <span className="bz-selector-nombre">{g.nombre}</span>
                <span className="bz-selector-correo">{g.miembros} {g.miembros === 1 ? 'persona' : 'personas'}{g.correo ? ` · ${g.correo}` : ''}</span>
              </span>
              {valor?.id === g.id && <Check className="h-4 w-4" aria-hidden="true" />}
            </button>
          ))}
          {estado.grupos.length > 0 && estado.personas.length > 0 && <p className="bz-selector-seccion">Personas</p>}
          {estado.personas.map((p) => (
            <button
              key={p.id}
              type="button"
              role="option"
              aria-selected={valor?.id === p.id}
              className="bz-selector-fila"
              onClick={() => elegir(p)}
            >
              <Avatar persona={p} tam={28} />
              <span className="min-w-0 flex-1">
                <span className="bz-selector-nombre">{p.nombre}</span>
                <span className="bz-selector-correo">{p.correo}</span>
              </span>
              {valor?.id === p.id && <Check className="h-4 w-4" aria-hidden="true" />}
            </button>
          ))}
          {!estado.cargando && estado.personas.length === 0 && estado.grupos.length === 0 && (
            <p className="bz-selector-vacio">{q ? 'Nadie coincide con la búsqueda' : 'No hay personas disponibles'}</p>
          )}
        </div>
        {estado.soloEquipo && <p className="bz-selector-nota">Solo el equipo de este buzón puede recibir casos.</p>}
      </PopoverContent>
    </Popover>
  );
}
